import BigNumber from 'bignumber.js'

import { behaviorOf } from '../fields/behavior'
import type { FieldContext } from '../fields/behavior/behavior'
import type { RowComparator, SortDirection } from '../fields/compare'
import {
  fieldKey,
  type Field,
  type RowData,
  type ViewGroupBy,
  type ViewSort,
} from '@/api/types'

/**
 * Ordering rows the way the backend does.
 *
 * The client sorts rows it already holds -- a row edited in place has to move to
 * where it now belongs without waiting for a refetch -- so this has to agree with
 * the backend exactly. Where the two disagree, the row lands in one place and
 * then jumps to another when the window is next refetched.
 */

/**
 * A sort or group-by, reduced to what ordering needs from either.
 *
 * `order` is optional because the API leaves it out when it is ascending, which
 * is also what the backend defaults it to.
 */
export interface OrderTerm {
  field: number
  order?: SortDirection
}

/**
 * Builds the comparator for a view.
 *
 * Group-bys come before sorts because grouping is the coarser ordering: rows are
 * gathered into their groups first and ordered within them second.
 *
 * The last two terms are always the row's own `order` and then its id, and they
 * are what make the result total. Without them two rows the view cannot separate
 * would compare as equal, and `Array.prototype.sort` is free to leave them in
 * either order -- so the same data would render differently after a refetch.
 */
export function getRowSortFunction(
  sortings: readonly OrderTerm[],
  fields: readonly Field[],
  groupBys: readonly OrderTerm[] = [],
  context?: FieldContext,
): RowComparator {
  const comparators: RowComparator[] = []

  for (const term of [...groupBys, ...sortings]) {
    const field = fields.find((candidate) => candidate.id === term.field)
    if (field === undefined) continue

    const compare = behaviorOf(field.type).getSort(
      field,
      fieldKey(field.id),
      term.order ?? 'ASC',
      context,
    )
    // A view can name a sort on a field that has since become unsortable -- a
    // formula whose expression was changed, say. Skipping it leaves the rest of
    // the ordering intact, which is what the backend does with the same view.
    if (compare !== null) comparators.push(compare)
  }

  comparators.push(compareByOrder, compareById)

  return (a, b) => {
    for (const compare of comparators) {
      const result = compare(a, b)
      if (result !== 0) return result
    }
    return 0
  }
}

/**
 * Orders by the row's own position in the table.
 *
 * `order` is an arbitrary-precision decimal, and it is a string for that reason:
 * a row dropped between two others gets an order between theirs, so the values
 * grow digits rather than being renumbered. Reading one with `parseFloat` loses
 * exactly the digits that distinguish the two neighbours.
 */
export const compareByOrder: RowComparator = (a, b) => {
  const left = new BigNumber(a.order)
  const right = new BigNumber(b.order)
  if (left.isEqualTo(right)) return 0
  return left.isLessThan(right) ? -1 : 1
}

/** The final tie-break, so the ordering is total. */
export const compareById: RowComparator = (a, b) => a.id - b.id

/**
 * Reduces a view's sorts to the terms ordering needs.
 *
 * The schema has `order` as optional -- it has a default on the backend, so it
 * may be omitted when creating one -- but a sort that comes back without it is
 * ascending, which is that same default.
 */
export function sortTerms(sortings: readonly ViewSort[]): OrderTerm[] {
  return sortings.map((sort) => ({ field: sort.field, order: sort.order ?? 'ASC' }))
}

/** Reduces a view's group-bys to the terms ordering needs. */
export function groupByTerms(groupBys: readonly ViewGroupBy[]): OrderTerm[] {
  return groupBys.map((groupBy) => ({
    field: groupBy.field,
    order: groupBy.order ?? 'ASC',
  }))
}

/**
 * Where a row belongs among rows that are already in order.
 *
 * Reported as the id of the row it follows rather than as an index, because the
 * two callers do not share an index space: the flat window counts rows from the
 * top of the table, and a board counts them within one stack.
 */
export interface RowInsertPosition {
  /** The row this one goes after, or null when it sorts first. */
  anchorRowId: number | null
  /** Its position among the existing rows plus itself. */
  sortedIndex: number
  isFirst: boolean
  isLast: boolean
}

export function computeRowInsertPosition(
  row: RowData,
  existingRows: readonly RowData[],
  compare: RowComparator,
): RowInsertPosition {
  const sorted = [...existingRows, row].sort(compare)
  const sortedIndex = sorted.findIndex((candidate) => candidate.id === row.id)
  const previous = sortedIndex > 0 ? sorted[sortedIndex - 1] : undefined

  return {
    anchorRowId: previous?.id ?? null,
    sortedIndex,
    isFirst: sortedIndex === 0,
    isLast: sortedIndex === sorted.length - 1,
  }
}

/**
 * The `order` values rows inserted between two others would plausibly get.
 *
 * A row created here appears before the server has named it, and it needs an
 * `order` at once, because that is what the comparator falls back to: a view
 * with no sorts is ordered by nothing else. A placeholder that ignores where
 * the row was put is therefore not neutral -- it decides the row's place, and
 * decides it wrongly. The value here is not the one the backend will choose,
 * and does not need to be; it needs only to sort where the row was put, so
 * that nothing concludes the row has moved before it has been anywhere.
 *
 * `null` for either neighbour means there is none to be seen -- the top or the
 * bottom of the view, or the edge of the loaded window, which are the same
 * thing as far as ordering against what is held goes.
 */
export function placeholderOrders(
  previous: string | null,
  next: string | null,
  count: number,
): string[] {
  if (count <= 0) return []

  // Cloned rather than configured globally: the division below is the only
  // place in the application that needs digits past `BigNumber`'s default, and
  // widening it everywhere would change how every other decimal reads.
  const Decimal = BigNumber.clone({ DECIMAL_PLACES: 60 })

  const after = previous === null ? null : new Decimal(previous)
  const before = next === null ? null : new Decimal(next)

  if (before === null) {
    const base = after ?? new Decimal(0)
    return Array.from({ length: count }, (_, offset) => base.plus(offset + 1).toFixed())
  }

  // A row going above everything held still needs somewhere to sit, and one
  // whole unit below its follower is as good a guess as any -- the backend
  // will replace it within the round trip either way.
  const lower = after ?? before.minus(1)
  const step = before.minus(lower).dividedBy(count + 1)
  return Array.from({ length: count }, (_, offset) =>
    lower.plus(step.multipliedBy(offset + 1)).toFixed(),
  )
}
